import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import GlobalNavigation from '../components/GlobalNavigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plane, Upload, AlertCircle, Luggage, ArrowRight, MapPin, Clock, Calendar, DollarSign, Camera, FileUp, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { flag } from '../utils/countryFlags';

// Pays disponibles
const COUNTRIES = ['Canada', 'Cameroun'];

// Villes par pays
const CITIES_BY_COUNTRY = {
  'Canada': ['Montreal', 'Longueuil', 'Contrecoeur', 'Sorel-Tracy', 'Québec', 'Trois-Rivières', 'Gatineau', 'Sherbrooke'],
  'Cameroun': ['Douala', 'Yaoundé']
};

// Points de collecte/dépôt par pays
const PICKUP_CITIES_BY_COUNTRY = {
  'Canada': ['Montreal', 'Longueuil', 'Contrecoeur', 'Sorel-Tracy', 'Québec', 'Trois-Rivières', 'Gatineau', 'Sherbrooke'],
  'Cameroun': ['Douala', 'Yaoundé']
};

// Points de livraison par pays
const DELIVERY_CITIES_BY_COUNTRY = {
  'Canada': ['Montreal', 'Longueuil', 'Contrecoeur', 'Sorel-Tracy', 'Québec', 'Trois-Rivières', 'Gatineau', 'Sherbrooke'],
  'Cameroun': ['Douala', 'Yaoundé']
};

// Types de bagages disponibles
const BAGGAGE_TYPES = [
  { value: 'kilos', label: 'Kilos (kg)', unit: 'kg' },
  { value: 'enveloppes', label: 'Enveloppes (max 0,5kg)', unit: 'enveloppe(s)' },
  { value: 'sac_23kg', label: 'Sac de 23kg', unit: 'sac(s)' },
  { value: 'telephones', label: 'Telephones', unit: 'telephone(s)' },
  { value: 'ordinateurs', label: 'Ordinateurs', unit: 'ordinateur(s)' }
];

const CreateListingPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [listingId, setListingId] = useState(null);
  const [ticketFile, setTicketFile] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  
  const [baggageItems, setBaggageItems] = useState([
    { type: '', quantity: '', price: '' }
  ]);
  
  const [form, setForm] = useState({
    departure_country: '',
    departure_city: '',
    destination_country: '',
    destination_city: '',
    travel_date: '',
    expiration_date: '',
    airline: '',
    currency: 'CAD',
    description: '',
    pickup_cities: [],
    delivery_cities: [],
    delivery_delay: ''
  });

  const airlines = ['Air France', 'Air Sénégal', 'Royal Air Maroc', 'Turkish Airlines', 'Ethiopian Airlines', 'Emirates', 'Qatar Airways', 'Air Canada', 'Autre'];

  const toggleCity = (field, city) => {
    setForm(prev => {
      const current = prev[field];
      if (current.includes(city)) {
        return { ...prev, [field]: current.filter(c => c !== city) };
      }
      if (current.length >= 2) {
        toast.error('Maximum 2 villes');
        return prev;
      }
      return { ...prev, [field]: [...current, city] };
    });
  };

  // Fonctions pour gérer les types de bagages
  const addBaggageItem = () => {
    setBaggageItems([...baggageItems, { type: '', quantity: '', price: '' }]);
  };

  const removeBaggageItem = (index) => {
    if (baggageItems.length > 1) {
      setBaggageItems(baggageItems.filter((_, i) => i !== index));
    }
  };

  const updateBaggageItem = (index, field, value) => {
    const updated = [...baggageItems];
    updated[index][field] = value;
    setBaggageItems(updated);
  };

  const getAvailableBaggageTypes = (currentIndex) => {
    const selectedTypes = baggageItems
      .filter((_, i) => i !== currentIndex)
      .map(item => item.type);
    return BAGGAGE_TYPES.filter(t => !selectedTypes.includes(t.value));
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTicketFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setTicketPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setTicketPreview(null);
      }
    }
  };

  // Handle camera capture
  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Rename file to avoid special characters
      const timestamp = Date.now();
      const newFile = new File([file], `billet_${timestamp}.jpg`, { type: file.type });
      setTicketFile(newFile);
      const reader = new FileReader();
      reader.onload = (e) => setTicketPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Clear file selection
  const clearFile = () => {
    setTicketFile(null);
    setTicketPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const validItems = baggageItems.filter(item => 
      item.type && item.quantity && item.price
    );
    
    if (validItems.length === 0) {
      toast.error('Ajoutez au moins un type de bagage avec quantité et prix');
      return;
    }

    if (!form.departure_country || !form.departure_city || !form.destination_country || 
        !form.destination_city || !form.travel_date || !form.expiration_date || !form.airline || 
        form.pickup_cities.length === 0 || form.delivery_cities.length === 0 || !form.delivery_delay) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }

    if (form.departure_country === form.destination_country) {
      toast.error('Le pays de départ doit être différent du pays de destination');
      return;
    }

    // Validation: expiration_date must be <= travel_date
    if (form.expiration_date > form.travel_date) {
      toast.error('La date limite de collecte ne peut pas être après la date de voyage');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(auth.API + '/listings', {
        ...form,
        baggage_items: validItems.map(item => ({
          type: item.type,
          quantity: parseFloat(item.quantity),
          price_per_unit: parseFloat(item.price)
        })),
        pickup_cities: form.pickup_cities,
        delivery_cities: form.delivery_cities
      }, {
        headers: { Authorization: 'Bearer ' + auth.token }
      });
      
      setListingId(response.data.id);
      toast.success('Annonce créée !');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const [ticketUploaded, setTicketUploaded] = useState(false);

  // Validate filename for special characters
  const validateFilename = (filename) => {
    const specialChars = /[éèêëàâäùûüôöîïçÉÈÊËÀÂÄÙÛÜÔÖÎÏÇ]/;
    return !specialChars.test(filename);
  };

  const handleUpload = async () => {
    if (!ticketFile) {
      toast.error('Sélectionnez un fichier ou prenez une photo');
      return;
    }
    
    // Check for special characters in filename
    if (!validateFilename(ticketFile.name)) {
      toast.error(
        'Le nom du fichier contient des caractères spéciaux (accents). Veuillez renommer votre fichier sans accents.',
        { duration: 6000 }
      );
      return;
    }
    
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', ticketFile);
      await axios.post(auth.API + '/listings/' + listingId + '/upload-ticket', fd, {
        headers: { Authorization: 'Bearer ' + auth.token, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Billet uploadé avec succès !');
      setTicketUploaded(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors du téléchargement';
      toast.error(errorMsg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSkipTicket = () => {
    toast.info('Vous pourrez ajouter le billet plus tard depuis votre espace vendeur');
    setStep(3);
  };

  const handleFinalSubmit = () => {
    toast.success('Annonce soumise pour approbation !');
    setStep(3);
  };

  if (!auth.user?.terms_accepted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <GlobalNavigation />
        <div className="max-w-2xl mx-auto px-4 py-24">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <h2 className="font-heading text-2xl font-bold mb-2">Acceptez les conditions</h2>
              <Button onClick={() => navigate('/verify')}>Continuer</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <Navbar />
        <GlobalNavigation />
        <div className="max-w-2xl mx-auto px-4 py-24">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-4">
                Annonce en cours de validation
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Votre annonce a été soumise avec succès et est en attente de validation par l'équipe Kacos.
              </p>
              <div className="bg-muted/50 border rounded-lg p-4 mb-8 text-left max-w-md mx-auto">
                <p className="text-sm mb-2">
                  <strong>Prochaines étapes :</strong>
                </p>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Notre équipe va vérifier et valider votre annonce</li>
                  <li>Une fois validée, votre annonce sera <strong>visible par les acheteurs</strong> sur la plateforme</li>
                  <li>Vous recevrez un <strong>courriel de confirmation</strong> de Kacos</li>
                </ol>
                {!ticketUploaded && (
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                    <p className="text-sm text-yellow-400 font-medium">
                      Attention : Vous n'avez pas téléchargé votre billet d'avion. Une annonce sans billet risque d'être rejetée car il est impossible de vérifier l'authenticité du voyage.
                    </p>
                  </div>
                )}
              </div>
              <Button 
                className="btn-primary"
                onClick={() => navigate('/seller')}
                data-testid="go-to-listings-btn"
              >
                Voir mes annonces
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <Navbar />
        <GlobalNavigation />
        <div className="max-w-3xl mx-auto px-4 py-24">
          <h1 className="font-heading text-3xl font-bold mb-8">Ajouter le billet d'avion</h1>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />Billet d'avion
              </CardTitle>
              <CardDescription>
                Uploadez ou photographiez votre billet pour vérification par l'administrateur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!ticketUploaded ? (
                <>
                  {/* File preview */}
                  {ticketFile && (
                    <div className="mb-4 p-4 border rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {ticketPreview ? (
                            <img src={ticketPreview} alt="Aperçu" className="w-16 h-16 object-cover rounded" />
                          ) : (
                            <div className="w-16 h-16 bg-primary/10 rounded flex items-center justify-center">
                              <FileUp className="w-8 h-8 text-primary" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{ticketFile.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {(ticketFile.size / 1024 / 1024).toFixed(2)} Mo
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={clearFile}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {!validateFilename(ticketFile.name) && (
                        <p className="text-red-500 text-sm mt-2">
                          Le nom contient des accents - veuillez renommer le fichier ou utiliser l'appareil photo
                        </p>
                      )}
                    </div>
                  )}

                  {/* Upload options */}
                  {!ticketFile && (
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {/* File upload */}
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors" 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input 
                          ref={fileInputRef} 
                          type="file" 
                          accept=".pdf,.jpg,.jpeg,.png" 
                          className="hidden" 
                          onChange={handleFileSelect} 
                        />
                        <FileUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">Choisir un fichier</p>
                        <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG</p>
                      </div>

                      {/* Camera capture */}
                      <div 
                        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors" 
                        onClick={() => cameraInputRef.current?.click()}
                      >
                        <input 
                          ref={cameraInputRef} 
                          type="file" 
                          accept="image/*" 
                          capture="environment"
                          className="hidden" 
                          onChange={handleCameraCapture} 
                        />
                        <Camera className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">Prendre une photo</p>
                        <p className="text-xs text-muted-foreground mt-1">Utilisez votre appareil photo</p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground text-center mb-4">
                    Formats acceptés : PDF, JPG, PNG (max 10 Mo)
                  </p>

                  {/* Action buttons */}
                  <div className="space-y-3">
                    {ticketFile && (
                      <Button 
                        className="w-full btn-primary" 
                        onClick={handleUpload} 
                        disabled={loading || !validateFilename(ticketFile.name)}
                      >
                        {loading ? 'Envoi en cours...' : 'Envoyer le billet'}
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={handleSkipTicket}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Ajouter plus tard
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Votre annonce ne sera visible qu'après validation par l'administrateur
                  </p>
                </>
              ) : (
                <>
                  <div className="border-2 border-green-500 bg-green-500/10 rounded-lg p-12 text-center">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="font-medium text-green-500 text-lg mb-2">Billet uploadé avec succès !</p>
                    <p className="text-sm text-muted-foreground">{ticketFile?.name}</p>
                  </div>
                  <div className="mt-6">
                    <Button className="w-full btn-primary" onClick={handleFinalSubmit}>
                      <ArrowRight className="w-5 h-5 mr-2" />
                      Soumettre l'annonce pour approbation
                    </Button>
                    <p className="text-sm text-muted-foreground text-center mt-3">
                      Votre annonce sera examinée par un administrateur avant d'être publiée.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <Navbar />
      <GlobalNavigation />
      <div className="max-w-3xl mx-auto px-4 py-24">
        <h1 className="font-heading text-3xl font-bold mb-8">Créer une annonce</h1>
        
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Departure - Pays puis Ville */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Plane className="w-4 h-4" />Pays de départ *
                  </Label>
                  <Select value={form.departure_country} onValueChange={v => {
                    const newForm = {...form, departure_country: v, departure_city: '', pickup_cities: []};
                    if (v === form.destination_country) {
                      newForm.destination_country = '';
                      newForm.destination_city = '';
                      newForm.delivery_cities = [];
                    }
                    setForm(newForm);
                  }}>
                    <SelectTrigger data-testid="departure-country"><SelectValue placeholder="Sélectionnez un pays" /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label>Ville de départ *</Label>
                  <Select 
                    value={form.departure_city} 
                    onValueChange={v => setForm({...form, departure_city: v})}
                    disabled={!form.departure_country}
                  >
                    <SelectTrigger data-testid="departure-city"><SelectValue placeholder="Sélectionnez une ville" /></SelectTrigger>
                    <SelectContent>
                      {(CITIES_BY_COUNTRY[form.departure_country] || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Destination - Pays puis Ville */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />Pays de destination *
                  </Label>
                  <Select value={form.destination_country} onValueChange={v => setForm({...form, destination_country: v, destination_city: '', delivery_cities: []})}>
                    <SelectTrigger data-testid="destination-country"><SelectValue placeholder="Sélectionnez un pays" /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.filter(c => c !== form.departure_country).map(c => <SelectItem key={c} value={c} textValue={c}>{flag(c)} {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <Label>Ville de destination *</Label>
                  <Select 
                    value={form.destination_city} 
                    onValueChange={v => setForm({...form, destination_city: v})}
                    disabled={!form.destination_country}
                  >
                    <SelectTrigger data-testid="destination-city"><SelectValue placeholder="Sélectionnez une ville" /></SelectTrigger>
                    <SelectContent>
                      {(CITIES_BY_COUNTRY[form.destination_country] || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date et compagnie */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />Date de voyage *
                  </Label>
                  <Input 
                    type="date" 
                    value={form.travel_date} 
                    onChange={e => {
                      const newTravelDate = e.target.value;
                      // Si la date limite est après la nouvelle date de voyage, la réinitialiser
                      const newExpDate = form.expiration_date > newTravelDate ? '' : form.expiration_date;
                      setForm({...form, travel_date: newTravelDate, expiration_date: newExpDate});
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="travel-date"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />Date limite de collecte *
                  </Label>
                  <Input 
                    type="date" 
                    value={form.expiration_date} 
                    onChange={e => setForm({...form, expiration_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    max={form.travel_date || undefined}
                    disabled={!form.travel_date}
                    data-testid="expiration-date"
                  />
                  <p className="text-xs text-muted-foreground">
                    Après cette date, l'annonce sera automatiquement fermée
                  </p>
                </div>
              </div>

              {/* Compagnie aérienne */}
              <div className="space-y-3">
                <Label>Compagnie aérienne *</Label>
                <Select value={form.airline} onValueChange={v => setForm({...form, airline: v})}>
                  <SelectTrigger data-testid="airline"><SelectValue placeholder="Sélectionnez" /></SelectTrigger>
                  <SelectContent>
                    {airlines.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Devise — moved above baggage types */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />Devise
                </Label>
                <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD - Dollar canadien</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="USD">USD - Dollar américain</SelectItem>
                    <SelectItem value="XAF">XAF - Franc CFA</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Types de bagages multiples */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Luggage className="w-4 h-4" />Types de bagages disponibles *
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={addBaggageItem}>
                    + Ajouter un type
                  </Button>
                </div>
                
                {baggageItems.map((item, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">Type {index + 1}</Badge>
                      {baggageItems.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeBaggageItem(index)}>
                          Supprimer
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Type de bagage</Label>
                        <Select value={item.type} onValueChange={v => updateBaggageItem(index, 'type', v)}>
                          <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                          <SelectContent>
                            {getAvailableBaggageTypes(index).map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Quantité</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          step="0.5"
                          placeholder="Ex: 10" 
                          value={item.quantity}
                          onChange={e => updateBaggageItem(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Prix / unité ({form.currency})</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          step="0.5"
                          placeholder="Ex: 8" 
                          value={item.price}
                          onChange={e => updateBaggageItem(index, 'price', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Points de collecte et livraison — multi-city (max 2) */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />Point de collecte (départ) *
                    <span className="text-xs text-muted-foreground ml-auto">max 2 villes</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {(PICKUP_CITIES_BY_COUNTRY[form.departure_country] || []).map(c => (
                      <Badge
                        key={c}
                        variant={form.pickup_cities.includes(c) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${form.pickup_cities.includes(c) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                        onClick={() => toggleCity('pickup_cities', c)}
                      >
                        {c}
                      </Badge>
                    ))}
                  </div>
                  {!form.departure_country && (
                    <p className="text-xs text-muted-foreground">Sélectionnez d'abord un pays de départ</p>
                  )}
                </div>
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    Point de livraison (destination) *
                    <span className="text-xs text-muted-foreground ml-auto">max 2 villes</span>
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {(DELIVERY_CITIES_BY_COUNTRY[form.destination_country] || []).map(c => (
                      <Badge
                        key={c}
                        variant={form.delivery_cities.includes(c) ? "default" : "outline"}
                        className={`cursor-pointer transition-colors ${form.delivery_cities.includes(c) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                        onClick={() => toggleCity('delivery_cities', c)}
                      >
                        {c}
                      </Badge>
                    ))}
                  </div>
                  {!form.destination_country && (
                    <p className="text-xs text-muted-foreground">Sélectionnez d'abord un pays de destination</p>
                  )}
                </div>
              </div>

              {/* Délai de livraison */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />Délai de livraison après arrivée *
                </Label>
                <Select value={form.delivery_delay} onValueChange={v => setForm({...form, delivery_delay: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionnez un délai" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_day">Même jour</SelectItem>
                    <SelectItem value="1_day">1 jour</SelectItem>
                    <SelectItem value="2_3_days">2-3 jours</SelectItem>
                    <SelectItem value="1_week">1 semaine</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-3">
                <Label>Notes supplémentaires</Label>
                <Textarea 
                  placeholder="Informations complémentaires..." 
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                />
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? 'Création...' : 'Continuer'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateListingPage;
